import type { FC, ReactNode } from 'react'

import { ArrowUp, ArrowUpRight } from 'lucide-react'

import { Logo } from '@/components/generals/logo'
import { FooterMark } from '@/components/generals/footer-mark'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'
import { TransitionLink } from '@/components/generals/transition-link'

import {
    BOOKING_HREF,
    contact,
    fullNav,
    openingHours,
    siteConfig
} from '@/constants/site'

export const Footer: FC = (): ReactNode => {
    const { scrollTo } = useSmoothScroll()

    return (
        <footer className='relative z-10 overflow-hidden bg-charcoal text-bone'>
            <div className='px-5 pt-20 sm:px-8 lg:px-12 lg:pt-28'>
                <div className='grid gap-14 border-b border-bone/10 pb-16 lg:grid-cols-12 lg:gap-8'>
                    <div className='lg:col-span-5'>
                        <Logo className='mb-10' />

                        <p className='max-w-md font-serif text-[clamp(1.75rem,3vw,2.5rem)] leading-[1.15] text-bone/90'>
                            Tee sheets open <em>twenty-one days</em> ahead for members and fourteen for guests.
                        </p>

                        <TransitionLink
                            href={BOOKING_HREF}
                            className='mt-8 inline-flex h-12 items-center gap-3 rounded-full bg-bone px-6 text-[12px] font-semibold uppercase tracking-[0.16em] text-charcoal transition-colors hover:bg-ochre'
                        >
                            Book a tee time
                            <ArrowUpRight className='size-4' aria-hidden='true' />
                        </TransitionLink>
                    </div>

                    <div className='grid grid-cols-1 gap-10 min-[480px]:grid-cols-2 lg:col-span-7 lg:grid-cols-3'>
                        <div>
                            <h2 className='mb-4 font-mono text-[11px] uppercase tracking-[0.24em] text-bone/45'>
                                Visit
                            </h2>

                            <address className='not-italic leading-relaxed text-bone/80'>
                                {contact.addressLines.map((line) => (
                                    <span key={line} className='block'>
                                        {line}
                                    </span>
                                ))}
                            </address>

                            <a
                                href={`mailto:${contact.email}`}
                                className='mt-4 inline-block break-all text-bone/80 underline decoration-bone/25 underline-offset-4 transition-colors hover:text-ochre'
                            >
                                {contact.email}
                            </a>
                        </div>

                        <div>
                            <h2 className='mb-4 font-mono text-[11px] uppercase tracking-[0.24em] text-bone/45'>
                                Hours
                            </h2>

                            <dl className='space-y-3'>
                                {openingHours.map((item) => (
                                    <div key={item.label}>
                                        <dt className='text-bone/80'>
                                            {item.label}
                                        </dt>

                                        <dd className='text-sm text-bone/50'>
                                            {item.value}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>

                        <nav aria-label='Footer'>
                            <h2 className='mb-4 font-mono text-[11px] uppercase tracking-[0.24em] text-bone/45'>
                                Explore
                            </h2>

                            <ul className='grid grid-cols-2 gap-x-6 gap-y-2 lg:grid-cols-1'>
                                {fullNav.map((link) => (
                                    <li key={link.href}>
                                        <TransitionLink
                                            href={link.href}
                                            className='text-bone/70 transition-colors hover:text-bone'
                                        >
                                            {link.label}
                                        </TransitionLink>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </div>

                <div className='flex flex-col gap-4 py-6 text-xs text-bone/45 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © 2026 {siteConfig.name}. All rights reserved.
                    </p>

                    <p className='font-mono uppercase tracking-[0.2em]'>
                        {contact.coordinates}
                    </p>

                    <button
                        type='button'
                        onClick={() => scrollTo(0, { duration: 2.2 })}
                        className='inline-flex items-center gap-2 self-start uppercase tracking-[0.18em] text-bone/70 transition-colors hover:text-bone sm:self-auto'
                    >
                        Back to top
                        <ArrowUp className='size-4' aria-hidden='true' />
                    </button>
                </div>
            </div>

            <FooterMark />
        </footer>
    )
}
